import { useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  ButtonLink,
  ErrorBanner,
  Panel,
  cn,
} from "../../../lib/ui";
import { useAuth } from "../../../lib/auth";
import {
  trackProductEvent,
  peekUpgradeSource,
} from "../../../lib/analytics";

const ACCOUNT_QUERY = gql`
  query AccountQuery {
    getAccount {
      id
      name
      plan
      hasBilling
      sendCount
    }
  }
`;

const CHECKOUT = gql`
  mutation StartCheckout(
    $plan: String!
    $upgradeSource: String
    $limitType: String
  ) {
    startCheckout(
      input: {
        plan: $plan
        upgradeSource: $upgradeSource
        limitType: $limitType
      }
    )
  }
`;

export type Highlight = {
  text: string;
  included?: boolean;
};

export type BillingInterval = "monthly" | "yearly";

export type PlanDef = {
  name: string;
  id: string;
  highlights: Highlight[];
  /** Monthly list price (null for Enterprise). */
  cost: string | null;
  /** Monthly equivalent when billed annually (2 months free). */
  yearlyCost: string | null;
  recommended?: boolean;
  isEnterprise?: boolean;
};

export const plans: PlanDef[] = [
  {
    name: "Free",
    id: "FREE",
    highlights: [
      { text: "3 templates" },
      { text: "10 exports / mo" },
      { text: "10 test emails / day" },
      { text: "1 user" },
      { text: "Dark mode preview" },
      { text: "2 AI templates / mo" },
      { text: "SendGrid & SES integration" },
      { text: "0 live client previews", included: false },
      { text: "0GB images", included: false },
    ],
    cost: "0",
    yearlyCost: "0",
  },
  {
    name: "Solo",
    id: "PRO",
    highlights: [
      { text: "30 templates" },
      { text: "Unlimited exports" },
      { text: "100 test emails / day" },
      { text: "1 user" },
      { text: "10GB images" },
      { text: "50 AI / mo" },
      { text: "40 live client previews / mo" },
      { text: "SendGrid & SES integration" },
    ],
    cost: "29",
    yearlyCost: "24",
    recommended: true,
  },
  {
    name: "Pro",
    id: "TEAM",
    highlights: [
      { text: "200 templates" },
      { text: "Unlimited exports" },
      { text: "500 test emails / day" },
      { text: "5 users" },
      { text: "100GB images" },
      { text: "250 AI / mo" },
      { text: "150 live client previews / mo" },
      { text: "SendGrid & SES integration" },
      { text: "Priority support" },
    ],
    cost: "79",
    yearlyCost: "66",
  },
  {
    name: "Enterprise",
    id: "ENTERPRISE",
    highlights: [
      { text: "Unlimited templates" },
      { text: "Unlimited exports" },
      { text: "Unlimited test emails / day" },
      { text: "Unlimited users" },
      { text: "SendGrid & SES integration" },
      { text: "Enterprise integrations" },
      { text: "API access" },
      { text: "Custom live preview limits" },
      { text: "Priority support" },
    ],
    cost: null,
    yearlyCost: null,
    isEnterprise: true,
  },
];

export const checkoutPlanKey = (
  planId: string,
  interval: BillingInterval
): string => {
  if (interval === "yearly" && (planId === "PRO" || planId === "TEAM")) {
    return `${planId}_YEARLY`;
  }
  return planId;
};

export const BillingIntervalToggle = ({
  value,
  onChange,
}: {
  value: BillingInterval;
  onChange: (next: BillingInterval) => void;
}) => (
  <div className="mt-8 flex flex-col items-center gap-2">
    <div
      role="radiogroup"
      aria-label="Billing interval"
      className="inline-flex rounded-lg border border-subtle bg-surface p-0.5"
    >
      {(
        [
          { id: "monthly", label: "Monthly" },
          { id: "yearly", label: "Yearly" },
        ] as const
      ).map((option) => {
        const selected = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.id)}
            className={cn(
              "relative rounded-md px-3.5 py-1.5 text-sm font-medium tracking-tight transition-colors duration-150 ease-out",
              selected
                ? "bg-blue text-white"
                : "text-neutral-600 hover:text-neutral-900"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
    <p
      className={cn(
        "text-xs font-medium tracking-tight transition-opacity duration-150 ease-out",
        value === "yearly" ? "text-pink-dark opacity-100" : "opacity-0"
      )}
      aria-hidden={value !== "yearly"}
    >
      2 months free
    </p>
  </div>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    className={cn("flex-shrink-0 h-4 w-4 text-green-500", className)}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const XIcon = () => (
  <svg
    className="flex-shrink-0 h-4 w-4 text-neutral-400"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

export const PlanColumn = ({
  plan,
  billingInterval,
  loading = false,
  account,
  createRedirectLink,
  error,
  isSubmitting,
  submittingPlanId,
}: {
  plan: PlanDef;
  billingInterval: BillingInterval;
  loading?: boolean;
  account?: { plan?: string };
  createRedirectLink: (planId: string) => (e: React.MouseEvent) => void;
  error?: string;
  isSubmitting: boolean;
  submittingPlanId: string | null;
}) => {
  const isCurrentPlan = account?.plan === plan.id;
  const isEnterprise = plan.isEnterprise;
  const isFree = plan.id === "FREE";
  const isRecommended = !!plan.recommended;
  const isThisSubmitting = isSubmitting && submittingPlanId === plan.id;
  const isYearly = billingInterval === "yearly";
  const displayCost =
    isYearly && plan.yearlyCost != null ? plan.yearlyCost : plan.cost;
  const checkoutKey = checkoutPlanKey(plan.id, billingInterval);

  let ctaLabel = "Get started";
  let ctaHref = "#";
  let ctaVariant: "primary" | "upgrade" | "outline" = "outline";
  let ctaDisabled = false;
  let ctaOnClick: ((e: React.MouseEvent) => void) | undefined;

  if (isCurrentPlan) {
    ctaLabel = "Current plan";
    ctaVariant = "outline";
    ctaDisabled = true;
  } else if (isEnterprise) {
    ctaLabel = "Contact us to discuss";
    ctaHref = "mailto:hello@postdrop.io";
    ctaVariant = "outline";
  } else if (isFree) {
    ctaLabel = "Contact us to downgrade";
    ctaHref = "mailto:hello@postdrop.io?subject=Downgrade to Free";
    ctaVariant = "outline";
  } else if (isRecommended) {
    ctaLabel = isThisSubmitting ? "Redirecting…" : `Get ${plan.name}`;
    ctaVariant = "upgrade";
    ctaOnClick = createRedirectLink(checkoutKey);
    ctaDisabled = isSubmitting || loading;
  } else {
    ctaLabel = isThisSubmitting ? "Redirecting…" : `Get ${plan.name}`;
    ctaVariant = "outline";
    ctaOnClick = createRedirectLink(checkoutKey);
    ctaDisabled = isSubmitting || loading;
  }

  return (
    <Panel
      padding="none"
      className={cn(
        "relative flex h-full w-full flex-col",
        isRecommended && "ring-1 ring-blue border-blue/50"
      )}
    >
      {isRecommended && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-pink-dark text-white">
          Popular
        </span>
      )}

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <h2 className="text-base font-semibold tracking-tight text-neutral-900">
          {plan.name}
        </h2>

        <div className="mt-3 min-h-[3.25rem]">
          {isEnterprise ? (
            <p className="flex items-baseline gap-1">
              <span className="text-3xl font-semibold tracking-tight text-pink-dark">
                Custom
              </span>
            </p>
          ) : (
            <p className="flex items-baseline gap-1">
              <span className="text-3xl font-semibold tracking-tight text-pink-dark tabular-nums">
                ${displayCost}
              </span>
              <span className="text-sm text-neutral-500">/ mo</span>
            </p>
          )}
          <p
            className={cn(
              "mt-1 text-xs text-neutral-500 transition-opacity duration-150 ease-out",
              isYearly && !isFree && !isEnterprise ? "opacity-100" : "invisible"
            )}
            aria-hidden={!(isYearly && !isFree && !isEnterprise)}
          >
            billed annually
          </p>
        </div>

        <ul className="mt-5 space-y-2.5 flex-1">
          {plan.highlights.map((item) => {
            const included = item.included !== false;
            return (
              <li className="flex gap-2.5" key={item.text}>
                {included ? <CheckIcon /> : <XIcon />}
                <span
                  className={cn(
                    "text-sm leading-snug",
                    included ? "text-neutral-600" : "text-neutral-400"
                  )}
                >
                  {item.text}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mt-6">
          <ButtonLink
            href={ctaHref}
            variant={ctaVariant}
            disabled={ctaDisabled}
            onClick={ctaOnClick}
            className="w-full justify-center"
          >
            {ctaLabel}
          </ButtonLink>
          {error && (
            <div className="mt-3">
              <ErrorBanner title={error} />
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
};

type PlansGridProps = {
  /** Optional heading shown above the interval toggle (modal). */
  heading?: React.ReactNode;
  className?: string;
};

/**
 * Interval toggle + plan columns with Stripe checkout.
 * Shared by `/plans` and the full-page plans modal.
 */
export function PlansGrid({ heading, className }: PlansGridProps) {
  const { user } = useAuth();
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("monthly");
  const [planErrors, setPlanError] = useState<Record<string, string>>({});
  const [isSubmitting, setSubmitting] = useState(false);
  const [submittingPlanId, setSubmittingPlanId] = useState<string | null>(null);
  const [startCheckout] = useMutation(CHECKOUT);
  const { loading, data } = useQuery(ACCOUNT_QUERY, {
    ssr: false,
    skip: !user,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });
  const isLoading = !data?.getAccount && loading;

  const createRedirectLink =
    (checkoutKey: string) => async (e: React.MouseEvent) => {
      e.preventDefault();
      const planColumnId = checkoutKey.replace(/_YEARLY$/, "");
      if (data?.getAccount?.hasBilling || planColumnId === "FREE") {
        return window.confirm(
          "We're working on support for automatically changing subscriptions. For now, please contact hello@postdrop for account downgrades or upgrades."
        );
      }
      setSubmitting(true);
      setSubmittingPlanId(planColumnId);
      try {
        const upgrade = peekUpgradeSource();
        trackProductEvent("Checkout Started", {
          plan: checkoutKey,
          upgrade_source: upgrade?.source,
          limit_type: upgrade?.limit_type,
        });
        const result = await startCheckout({
          variables: {
            plan: checkoutKey,
            upgradeSource: upgrade?.source ?? null,
            limitType: upgrade?.limit_type ?? null,
          },
        });
        const url = result?.data?.startCheckout;
        if (!url) {
          throw new Error("Checkout session URL was not returned.");
        }
        window.location.assign(url);
      } catch (error) {
        setPlanError({
          [planColumnId]:
            error.message || "An error occurred during the checkout process.",
        });
        setSubmitting(false);
        setSubmittingPlanId(null);
      }
    };

  return (
    <div className={className}>
      {heading}
      <BillingIntervalToggle
        value={billingInterval}
        onChange={setBillingInterval}
      />
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 items-stretch">
        {plans.map((plan) => (
          <PlanColumn
            key={plan.id}
            plan={plan}
            billingInterval={billingInterval}
            loading={isLoading}
            account={data?.getAccount}
            createRedirectLink={createRedirectLink}
            error={planErrors[plan.id]}
            isSubmitting={isSubmitting}
            submittingPlanId={submittingPlanId}
          />
        ))}
      </div>
    </div>
  );
}
